

import React, { useState, useEffect } from "react";
import axios from "axios";
import { UserPlus, Edit3, Trash2, ShieldCheck, Shield, User, X } from "lucide-react";

// ─── Constants & Meta Definitions ───────────────────────────────────────────
const ROLES = [
  { key: "super_admin", label: "Super Admin", icon: <ShieldCheck size={16} />, color: "#6d28d9", bg: "#f5f3ff", border: "#ddd6fe", desc: "Full platform access — manages tables, templates, pricing, system configurations, and all users.", },
  { key: "client_admin", label: "Client Admin", icon: <Shield size={16} />, color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe", desc: "Restricted to managing group contacts, sending messages, and viewing analytics.", },
  { key: "user", label: "Normal User", icon: <User size={16} />, color: "#047857", bg: "#ecfdf5", border: "#a7f3d0", desc: "Can create tables and manage groups within assigned scope. No access to system config.", },
];

const ALL_MODULES = [
  { module: "Dashboard", items: [{ label: "Dashboard", key: "dashboard" }] },
  {
    module: "Manage Contacts",
    items: [
      { label: "Add Groups", key: "add groups" },
      { label: "Add Contact", key: "add contact" },
      { label: "Contact List", key: "contact list" },
    ]
  },
  { module: "Send Message", items: [{ label: "Send Message", key: "send message" }] },
  {
    module: "Message Templates",
    items: [
      { label: "Templates", key: "templates" },
      { label: "PP Appointment", key: "pp appointment" },
      { label: "US Visa Appointment", key: "us visa appointment" },
      { label: "Labour License", key: "labour license" },
      { label: "Trade License", key: "trade license" },
      { label: "Birthday Greetings", key: "birthday greetings" },
      { label: "Festival Greetings", key: "festival greetings" },
    ]
  },
  { module: "Message Analytics", items: [{ label: "Message Analytics", key: "message analytics" }] },
  {
    module: "Table Config",
    items: [
      { label: "Table Create", key: "table create" },
      { label: "User Tables List", key: "user tables list" },
    ]
  },
  {
    module: "Bot Config",
    items: [
      { label: "Template Mapping", key: "template mapping" },
      { label: "Template Mapping List", key: "template mapping list" },
    ]
  },
  {
    module: "Column Mapping",
    items: [
      { label: "Column Mapping", key: "column mapping" },
      { label: "Language", key: "language" },
    ]
  },
  { module: "User Creation", items: [{ label: "Create a User", key: "create a user" }] },
  {
    module: "Groups",
    items: [
      { label: "CreateGroup", key: "creategroup" },
      { label: "Group Message", key: "group message" },
      { label: "Group Invite", key: "group invite" },
      { label: "GroupInfo", key: "groupinfo" },
    ]
  },
  { module: "Pricing & Billing", items: [{ label: "Pricing Plans", key: "pricing" }, { label: "Invoice History", key: "invoices" }] },
  { module: "System Config", items: [{ label: "System Settings", key: "sys_settings" }, { label: "API Keys", key: "api_keys" }] },
];

const ROLE_DEFAULTS = {
  super_admin: ALL_MODULES.flatMap((m) => m.items.map((i) => i.key)),
  client_admin: [
    "dashboard", "msg_analytics", "send_message", "msg_history",
    "upload_contact", "contact_list",
    "create_group", "group_message", "group_invite",
    "templates",
  ],
  user: [
    "dashboard",
    "send_message", "msg_history",
    "upload_contact", "contact_list",
    "create_table", "tables_list",
    "create_group", "group_message",
  ],
};

const ROLE_META = Object.fromEntries(ROLES.map((r) => [r.key, r]));
const allKeys = ALL_MODULES.flatMap((m) => m.items.map((i) => i.key));

// ─── Main Component ──────────────────────────────────────────────────────────

export default function UserManagement() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    wa_id: "",
    webhook_table: "",
    whatsappBusinessId: "",
    image: "",
    status: "active",
    isActive: 1,
    flag: 0,
    resettoken: ""
  });

  const [selectedRole, setSelectedRole] = useState("user");
  const [selectedPerms, setSelectedPerms] = useState(ROLE_DEFAULTS.user);
  const [users, setUsers] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const [activeTab, setActiveTab] = useState("directory"); // 'directory' or 'client_admins'
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [createdBy, setCreatedBy] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);

  useEffect(() => {
    loadTabData();

    const user = JSON.parse(sessionStorage.getItem("user"));
    if (user) {
      setCreatedBy(user.id);
      setCreateEmail(user.email);
    }

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeTab]);

  const isMobile = windowWidth < 640;

  // ─── Separate API Action Layer ──────────────────────────────────────────────

  const loadTabData = () => {
    setUsers([]);
    setCurrentPage(1);

    if (activeTab === "directory") {
      // Hits route mapping userMember table updates
      axios.get("https://botapi.emedha.in/pages/api/users")
        .then(res => setUsers(res.data))
        .catch(err => console.error("Error fetching general user directory:", err));
    } else if (activeTab === "client_admins") {
      // Hits separate specialized route mapping users table updates
      axios.get("https://botapi.emedha.in/pages/api/api/client-admins")
        .then(res => setUsers(res.data))
        .catch(err => console.error("Error fetching client admins:", err));
    }
  };

  // const handleSubmit = (e) => {
  //   if (e) e.preventDefault();

  //   if (!formData.name || !formData.email || !formData.mobile) {
  //     alert("Please fill out all required fields marked with *");
  //     return;
  //   }

  //   if (!editingId && !formData.password) {
  //     alert("Password field is required.");
  //     return;
  //   }

  //   if (activeTab === "directory") {
  //     // 1️⃣ USER DIRECTORY OPERATION (Targeting userMember Table APIs)
  //     const userPayload = {
  //       name: formData.name,
  //       email: formData.email,
  //       password: formData.password,
  //       mobile: formData.mobile,
  //       permissions: JSON.stringify(selectedPerms),
  //       createdBy: createdBy || null,
  //       creatEmail: createEmail || "",
  //       role: selectedRole
  //     };

  //     if (editingId === null) {
  //       axios.post("https://botapi.emedha.in/pages/api/users", userPayload)
  //         .then(() => {
  //           alert("🎉 Success: User added to Directory!");
  //           loadTabData();
  //           closeModal();
  //         })
  //         .catch(err => alert("❌ Error creating Directory user"));
  //     } else {
  //       axios.put(`https://botapi.emedha.in/pages/api/users/${editingId}`, userPayload)
  //         .then(() => {
  //           alert("🔄 Success: Directory user updated!");
  //           loadTabData();
  //           closeModal();
  //         })
  //         .catch(err => alert("❌ Error updating Directory user"));
  //     }

  //   } else {
  //     // 2️⃣ CLIENT ADMINS OPERATION (Targeting users Table APIs)
  //     const clientPayload = {
  //       name: formData.name,
  //       email: formData.email,
  //       password: formData.password,
  //       phone: formData.mobile, // Maps to backend 'phone' parameter field name
  //       wa_id: formData.wa_id,
  //       webhook_table: formData.webhook_table,
  //       whatsappBusinessId: formData.whatsappBusinessId,
  //       image: formData.image,
  //       status: formData.status || "pending",
  //       isActive: formData.isActive,
  //       flag: formData.flag,
  //       resettoken: formData.resettoken,
  //       p_id: createdBy || null,
  //       role: selectedRole,
  //       permissions: JSON.stringify(selectedPerms)
  //     };

  //     if (editingId === null) {
  //       axios.post("https://botapi.emedha.in/pages/api/client", clientPayload)
  //         .then(() => {
  //           alert("🎉 Success: Client Admin created successfully!");
  //           loadTabData();
  //           closeModal();
  //         })
  //         .catch(err => alert("❌ Error creating Client Admin entry"));
  //     } else {
  //       axios.put(`https://botapi.emedha.in/pages/api/client/${editingId}`, clientPayload)
  //         .then(() => {
  //           alert("🔄 Success: Client Admin parameters saved!");
  //           loadTabData();
  //           closeModal();
  //         })
  //         .catch(err => alert("❌ Error updating Client Admin entry"));
  //     }
  //   }
  // };



  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    if (!formData.name || !formData.email || !formData.mobile) {
      alert("Please fill out all required fields marked with *");
      return;
    }

    if (!editingId && !formData.password) {
      alert("Password field is required.");
      return;
    }

    if (activeTab === "directory") {
      const userPayload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        mobile: formData.mobile,
        permissions: JSON.stringify(selectedPerms),
        createdBy: createdBy || null,
        creatEmail: createEmail || "",
        role: selectedRole
      };

      if (editingId === null) {
        axios.post("https://botapi.emedha.in/pages/api/users", userPayload)
          .then(() => {
            alert("🎉 Success: User added to Directory!");
            loadTabData();
            closeModal();
          })
          .catch(err => alert("❌ Error creating Directory user"));
      } else {
        axios.put(`https://botapi.emedha.in/pages/api/users/${editingId}`, userPayload)
          .then(() => {
            alert("🔄 Success: Directory user updated!");
            loadTabData();
            closeModal();
          })
          .catch(err => alert("❌ Error updating Directory user"));
      }

    } else {
      // 2️⃣ CLIENT ADMINS OPERATION
      const clientPayload = {
        name: formData.name,
        email: formData.email,
        phone: formData.mobile,
        wa_id: formData.wa_id,
        webhook_table: formData.webhook_table,
        whatsappBusinessId: formData.whatsappBusinessId,
        image: formData.image,
        status: formData.status || "pending",
        isActive: formData.isActive,
        flag: formData.flag,
        resettoken: formData.resettoken,
        role: selectedRole,
        permissions: JSON.stringify(selectedPerms)
      };

      // Only include password and p_id if creating a brand new record
      if (editingId === null) {
        clientPayload.password = formData.password;
        clientPayload.p_id = createdBy || null;
      }

      if (editingId === null) {
        axios.post("https://botapi.emedha.in/pages/api/client", clientPayload)
          .then(() => {
            alert("🎉 Success: Client Admin created successfully!");
            loadTabData();
            closeModal();
          })
          .catch(err => alert("❌ Error creating Client Admin entry"));
      } else {
        axios.put(`https://botapi.emedha.in/pages/api/client/${editingId}`, clientPayload)
          .then(() => {
            alert("🔄 Success: Client Admin parameters saved!");
            loadTabData();
            closeModal();
          })
          .catch(err => alert("❌ Error updating Client Admin entry"));
      }
    }
  };
  const handleDelete = (id) => {
    if (!window.confirm("Are you absolutely sure you want to remove this profile?")) return;

    if (activeTab === "directory") {
      axios.delete(`https://botapi.emedha.in/pages/api/users/${id}`)
        .then(() => { alert("🗑️ Deleted from Directory"); loadTabData(); })
        .catch(err => console.error(err));
    } else {
      axios.delete(`https://botapi.emedha.in/pages/api/client/${id}`)
        .then(() => { alert("🗑️ Client Admin record removed"); loadTabData(); })
        .catch(err => console.error(err));
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);

    // Setup form conditional map parameters safely supporting either backend layout format
    setFormData({
      name: user.name || "",
      email: user.email || "",
      mobile: user.mobile || user.phone || "",
      password: "",
      wa_id: user.wa_id || "",
      webhook_table: user.webhook_table || "",
      whatsappBusinessId: user.whatsappBusinessId || "",
      image: user.image || "",
      status: user.status || "active",
      isActive: user.isActive ?? 1,
      flag: user.flag ?? 0,
      resettoken: user.resettoken || ""
    });

    setSelectedRole(user.role || "user");
    setSelectedPerms(typeof user.permissions === 'string' ? JSON.parse(user.permissions || "[]") : (user.permissions || []));
    setIsModalOpen(true);
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    setSelectedPerms(ROLE_DEFAULTS[role] || []);
  };

  const togglePerm = (key) => {
    setSelectedPerms((prev) => prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]);
  };

  const toggleAll = (keys, checked) => {
    if (checked) setSelectedPerms((prev) => [...new Set([...prev, ...keys])]);
    else setSelectedPerms((prev) => prev.filter((p) => !keys.includes(p)));
  };

  const clearForm = () => {
    setFormData({ name: "", email: "", password: "", mobile: "", wa_id: "", webhook_table: "", whatsappBusinessId: "", image: "", status: "active", isActive: 1, flag: 0, resettoken: "" });
    setSelectedRole("user");
    setSelectedPerms(ROLE_DEFAULTS.user);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    clearForm();
  };

  const totalPages = Math.ceil(users.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRows = users.slice(startIndex, startIndex + itemsPerPage);

  // ─── Presentation Styles ──────────────────────────────────────────────────
  const theme = { fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", primary: "#2563eb", background: "#f8fafc", cardBg: "#ffffff", border: "#e2e8f0", textMain: "#0f172a", textMuted: "#64748b", destructive: "#dc2626" };

  const styles = {
    wrapper: { fontFamily: theme.fontFamily, backgroundColor: theme.background, minHeight: "100vh", display: "flex", flexDirection: "column", gap: "20px", color: theme.textMain },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
    title: { fontSize: "24px", fontWeight: "700", margin: 0, display: "flex", alignItems: "center", gap: "8px" },
    subtitle: { fontSize: "14px", color: theme.textMuted, margin: "4px 0 0 0" },
    tabBar: { display: "flex", gap: "8px", borderBottom: `2px solid ${theme.border}`, marginBottom: "8px" },
    tabButton: (isActive) => ({ padding: "10px 20px", fontSize: "14px", fontWeight: "600", color: isActive ? theme.primary : theme.textMuted, background: "none", border: "none", borderBottom: isActive ? `2px solid ${theme.primary}` : "2px solid transparent", cursor: "pointer", marginBottom: "-2px" }),
    grid3: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "12px" },
    formCard: { backgroundColor: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: "12px", padding: "20px" },
    formCardTitle: { fontSize: "16px", fontWeight: "700", margin: "0 0 16px 0" },
    inputGrid: { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: "16px" },
    inputGroup: { display: "flex", flexDirection: "column", gap: "6px" },
    label: { fontSize: "12px", fontWeight: "600" },
    input: { padding: "10px 12px", fontSize: "14px", borderRadius: "8px", border: `1px solid ${theme.border}`, outline: "none" },
    roleButton: (isActive, role) => ({ display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px", borderRadius: "12px", border: isActive ? `2px solid ${role.border}` : `2px solid ${theme.border}`, backgroundColor: isActive ? role.bg : "transparent", textAlign: "left", cursor: "pointer", width: "100%" }),
    matrixHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: `1px solid ${theme.border}`, backgroundColor: "#f8fafc" },
    matrixRow: { padding: "14px 20px", borderBottom: `1px solid ${theme.border}`, display: "flex", flexDirection: "column", gap: "10px" },
    badge: (bgColor, textColor) => ({ fontSize: "10px", fontWeight: "600", padding: "2px 8px", borderRadius: "9999px", backgroundColor: bgColor, color: textColor }),
    pill: (checked) => ({ display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 10px", borderRadius: "8px", border: `1px solid ${checked ? "rgba(37,99,235,0.3)" : theme.border}`, fontSize: "12px", fontWeight: "500", cursor: "pointer", backgroundColor: checked ? "#eff6ff" : "#f8fafc", color: checked ? theme.primary : theme.textMuted }),
    footerBar: { padding: "12px 20px", backgroundColor: "#f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center" },
    btnPrimary: { backgroundColor: theme.primary, color: "#fff", border: "none", borderRadius: "8px", padding: "10px 18px", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" },
    btnSecondary: { backgroundColor: "#fff", color: theme.textMain, border: `1px solid ${theme.border}`, borderRadius: "8px", padding: "10px 18px", fontSize: "13px", fontWeight: "600", cursor: "pointer" },
    table: { width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "13px" },
    th: { padding: "12px 20px", backgroundColor: "#f8fafc", borderBottom: `1px solid ${theme.border}`, fontSize: "11px", fontWeight: "600", color: theme.textMuted, textTransform: "uppercase" },
    td: { padding: "14px 20px", borderBottom: `1px solid ${theme.border}`, verticalAlign: "top" },
    actionBtn: () => ({ width: "28px", height: "28px", borderRadius: "8px", border: `1px solid ${theme.border}`, backgroundColor: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: theme.textMuted }),
    modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, padding: "20px" },
    modalContent: { backgroundColor: "#ffffff", borderRadius: "16px", width: "100%", maxWidth: "840px", maxHeight: "90vh", overflowY: "auto", display: "flex", flexDirection: "column", position: "relative" },
    modalHeader: { padding: "20px", borderBottom: `1px solid ${theme.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 10 }
  };

  return (
    <div style={styles.wrapper}>
      {/* ── Page Header ── */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}><UserPlus size={22} style={{ color: theme.primary }} /> Account Management</h1>
          <p style={styles.subtitle}>Manage profiles across User Directory and Client Admin segments.</p>
        </div>
      </div>

      {/* ── Tabs Segment ── */}
      <div style={styles.tabBar}>
        <button onClick={() => setActiveTab("directory")} style={styles.tabButton(activeTab === "directory")}>User Directory</button>
        <button onClick={() => setActiveTab("client_admins")} style={styles.tabButton(activeTab === "client_admins")}>Client Admins</button>
      </div>

      {/* ── Directory Table Display ── */}
      <div style={{ ...styles.formCard, padding: 0, overflow: "hidden" }}>
        <div style={styles.matrixHeader}>
          <p style={{ fontSize: "14px", fontWeight: "700", margin: 0 }}>{activeTab === "directory" ? "User Directory (userMember Table)" : "Client Admins (users Table)"}</p>
          <span style={styles.badge("#f1f5f9", theme.textMuted)}>{users.length} Total</span>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {["Name", "Email", "Mobile/Phone", "Role", "Permissions", "Actions"].map((h) => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentRows.map((u) => {
                const roleMeta = ROLE_META[u.role] || ROLE_META["user"];
                const userPermsArray = typeof u.permissions === "string" ? JSON.parse(u.permissions || "[]") : (u.permissions || []);
                return (
                  <tr key={u.id}>
                    <td style={{ ...styles.td, fontWeight: "600" }}>{u.name}</td>
                    <td style={{ ...styles.td, color: theme.textMuted }}>{u.email}</td>
                    <td style={{ ...styles.td }}>{u.mobile || u.phone || "—"}</td>
                    <td><span style={styles.badge(roleMeta.bg, roleMeta.color)}>{roleMeta.label}</span></td>
                    <td style={{ ...styles.td, maxWidth: "220px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {userPermsArray.slice(0, 3).map((p) => (
                          <span key={p} style={{ fontSize: "10px", padding: "2px 6px", backgroundColor: "#f1f5f9", borderRadius: "4px", color: theme.textMuted }}>{p}</span>
                        ))}
                        {userPermsArray.length > 3 && (
                          <span style={{ fontSize: "10px", padding: "2px 6px", backgroundColor: "#eff6ff", color: theme.primary }}>+{userPermsArray.length - 3} more</span>
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button onClick={() => handleEdit(u)} style={styles.actionBtn()} title="Edit"><Edit3 size={12} /></button>
                        <button onClick={() => handleDelete(u.id)} style={{ ...styles.actionBtn(), color: theme.destructive }} title="Delete"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={styles.footerBar}>
          <p style={{ fontSize: "12px", color: theme.textMuted }}>Showing {users.length === 0 ? 0 : startIndex + 1}–{Math.min(startIndex + itemsPerPage, users.length)} of {users.length}</p>
          <div style={{ display: "flex", gap: "4px" }}>
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} style={styles.btnSecondary}>Prev</button>
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={styles.btnSecondary}>Next</button>
          </div>
        </div>
      </div>


      {/* ── React Management Modal Wrapper ── */}
      {isModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={{ fontSize: "18px", fontWeight: "700", margin: 0 }}>{editingId ? `Edit Details (${activeTab === "directory" ? "Directory" : "Client Admin"})` : "Create New Account Profile"}</h2>
              <button onClick={closeModal} style={{ background: "none", border: "none", cursor: "pointer", color: theme.textMuted }}><X size={20} /></button>
            </div>

            <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "24px" }}>
              {/* Profile Details */}
              <div style={styles.formCard}>
                <h3 style={styles.formCardTitle}>Basic User Details</h3>
                <div style={styles.inputGrid}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Name <span style={{ color: theme.destructive }}>*</span></label>
                    <input type="text" placeholder="Enter full name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={styles.input} required />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Email Address <span style={{ color: theme.destructive }}>*</span></label>
                    <input type="email" placeholder="user@domain.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} style={styles.input} required />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Mobile / Phone <span style={{ color: theme.destructive }}>*</span></label>
                    <input type="text" placeholder="Enter contact number" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} style={styles.input} required />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Password {!editingId && <span style={{ color: theme.destructive }}>*</span>}</label>
                    <input type="password" placeholder={editingId ? "Leave empty to retain current value" : "••••••••"} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} style={styles.input} />
                  </div>
                </div>
              </div>

              <div style={styles.formCard}>
                <h3 style={styles.formCardTitle}>Assign Role Category</h3>
                <div style={styles.grid3}>
                  {ROLES.map((role) => {
                    const isActive = selectedRole === role.key;
                    return (
                      <button key={role.key} type="button" onClick={() => handleRoleChange(role.key)} style={styles.roleButton(isActive, role)}>
                        <span style={{ color: isActive ? role.color : theme.textMuted, display: "flex", marginTop: "2px" }}>{role.icon}</span>
                        <div>
                          <p style={{ fontSize: "14px", fontWeight: "700", margin: "0 0 2px 0", color: isActive ? role.color : theme.textMain }}>{role.label}</p>
                          <p style={{ fontSize: "11px", color: theme.textMuted, margin: 0 }}>{role.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Permission Tuning Panel */}
              <div style={{ ...styles.formCard, padding: 0, overflow: "hidden" }}>
                <div style={styles.matrixHeader}>
                  <p style={{ fontSize: "13px", fontWeight: "700", margin: 0 }}>Granular Feature Permissions</p>
                  <div style={{ display: "flex", gap: "8px", fontSize: "12px" }}>
                    <button type="button" onClick={() => toggleAll(allKeys, true)} style={{ border: "none", background: "none", color: theme.primary, cursor: "pointer" }}>Select All</button>
                    <button type="button" onClick={() => toggleAll(allKeys, false)} style={{ border: "none", background: "none", color: theme.textMuted, cursor: "pointer" }}>Clear All</button>
                  </div>
                </div>
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {ALL_MODULES.map((mod) => {
                    const modKeys = mod.items.map((i) => i.key);
                    const allChecked = modKeys.every((k) => selectedPerms.includes(k));
                    return (
                      <div key={mod.module} style={styles.matrixRow}>
                        <label style={{ fontSize: "12px", fontWeight: "700" }}>
                          <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(modKeys, e.target.checked)} style={{ marginRight: "6px" }} />
                          {mod.module}
                        </label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", paddingLeft: "20px" }}>
                          {mod.items.map((item) => {
                            const checked = selectedPerms.includes(item.key);
                            return (
                              <label key={item.key} style={styles.pill(checked)}>
                                <input type="checkbox" checked={checked} onChange={() => togglePerm(item.key)} style={{ display: "none" }} />{item.label}</label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sticky Action Footer */}
            <div style={{ ...styles.footerBar, position: "sticky", bottom: 0, borderTop: `1px solid ${theme.border}`, padding: "16px 20px" }}>
              <p style={{ fontSize: "12px", color: theme.textMuted }}>{selectedPerms.length} Active Modules Selected</p>
              <div style={{ display: "flex", gap: "10px" }}>
                <button type="button" onClick={closeModal} style={styles.btnSecondary}>Close</button>
                <button type="button" onClick={handleSubmit} style={styles.btnPrimary}>
                  {editingId ? "Save Changes" : "Create Account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}